import React, { useState, useEffect, useCallback, useRef } from 'react';
import { AgGridTable, Pagination, DateRangePicker } from 'components/common';
import { useDispatch, useSelector } from 'react-redux';
import { FaSearch, FaSyncAlt, FaTimes } from 'react-icons/fa';
import { FaFileExcel } from 'react-icons/fa6';
import { MdFilterAlt, MdCompareArrows } from 'react-icons/md';
import { getShopByChangeOrder, exportShopByChangeOrder } from 'redux/crm/report/reportSlice';
import { getActiveSalesUnits } from 'redux/crm/salesUnitSlice';
import AdvanceFilterDrawer, { getEmptyValues } from 'components/common/AdvanceFilterDrawer';
import SalesUnitGroupedDropdown from 'components/app/crm/shared/SalesUnitGroupedDropdown';
import { Link } from 'react-router-dom';
import { debounce } from 'helpers/debounce';
import NumberRenderer from 'components/common/ag-grid/NumberRenderer';
import JobFormatRenderer from 'components/app/crm/reports/components/JobFormatRenderer';
import DateFormatRenderer from 'components/app/crm/reports/components/DateFormatRenderer';
import CenteredHeaderGroup from 'components/common/ag-grid/CenteredHeaderGroup';
import dayjs from 'dayjs';

const THIS_MONTH_START = dayjs().startOf('month').format('YYYY-MM-DD');
const THIS_MONTH_END   = dayjs().endOf('month').format('YYYY-MM-DD');

const SEARCH_FIELD = 'phase_building_j_number_cont';

const FILTER_FIELDS = [
  { key: 'phase_name',    label: 'Phase Name',    type: 'text', paramKey: 'phase_name_cont',            tagName: 'Phase'     },
  { key: 'job_number',    label: 'Job Number',     type: 'text', paramKey: 'phase_building_j_number_cont', tagName: 'J#'      },
  { key: 'building_name', label: 'Building Name',  type: 'text', paramKey: 'phase_building_name_cont',   tagName: 'Building'  },
  { key: 'shop_requested',  label: 'Shop Request',   type: 'date_range_picker', paramKeys: ['phase_shop_requested_gteq',  'phase_shop_requested_lteq'],  tagName: 'Shop Req.'  },
  { key: 'shop_scheduled',  label: 'Shop Schedule',  type: 'date_range_picker', paramKeys: ['phase_shop_scheduled_gteq',  'phase_shop_scheduled_lteq'],  tagName: 'Shop Sch.'  },
  { key: 'shop_released',   label: 'Shop Release',   type: 'date_range_picker', paramKeys: ['phase_shop_released_gteq',   'phase_shop_released_lteq'],   tagName: 'Shop Rel.'  },
  { key: 'erc_requested',   label: 'Erection Request',  type: 'date_range_picker', paramKeys: ['phase_erc_requested_gteq',  'phase_erc_requested_lteq'],  tagName: 'ERC Req.'   },
  { key: 'erc_scheduled',   label: 'Erection Schedule', type: 'date_range_picker', paramKeys: ['phase_erc_scheduled_gteq',  'phase_erc_scheduled_lteq'],  tagName: 'ERC Sch.'   },
  { key: 'erc_released',    label: 'Erection Release',  type: 'date_range_picker', paramKeys: ['phase_erc_released_gteq',   'phase_erc_released_lteq'],   tagName: 'ERC Rel.'   },
  { key: 'created_by',      label: 'Created By',    type: 'text', paramKey: 'created_by_cont',           tagName: 'Created By' },
  { key: 'created_date',    label: 'Created Date',  type: 'date_range_picker', paramKeys: ['created_date_gteq', 'created_date_lteq'], tagName: 'Created' },
];

const COLUMN_DEFS = [
  {
  headerName: "",
  children: [
    { 
      headerName: 'ID#',
      field: 'id',
      minWidth: 60,
    },
    { 
      headerName: 'Job number',
      field: 'building.job_number',
      minWidth: 120,
      cellRenderer: JobFormatRenderer
    },
    { 
      headerName: 'Project name',
      field: 'project.name',
      minWidth: 200,
      cellRenderer: (params) => {
        const {project} = params.data
        return (
          <Link to={`/crm/projects/${project.id}`} target="_blank">{project.name}</Link>
        )
      }
    },
    { 
      headerName: 'Building',
      field: 'building.name',
      minWidth: 100,
    },
    { 
      headerName: 'CO',
      field: 'co',
      minWidth: 80,
    },
    { 
      headerName: 'Phase name',
      field: 'name',
      minWidth: 100
    },
    { 
      headerName: "BOM (M.T)",
      field: "bom_weight",
      minWidth: 130,
      cellRenderer: NumberRenderer
    }
  ]
},
{
  headerName: 'SHOP DRAWINGS',
  headerGroupComponent: CenteredHeaderGroup,
  children: [
    { headerName: "Request", field: "shop.shop_requested", minWidth: 130, cellRenderer: DateFormatRenderer },
    { headerName: "Schedule", field: "shop.shop_scheduled", minWidth: 130, cellRenderer: DateFormatRenderer },
    { headerName: "Released", field: "shop_released", minWidth: 130, cellRenderer: DateFormatRenderer }
  ]
},
{
  headerName: 'ERECTION DRAWINGS',
  headerGroupComponent: CenteredHeaderGroup,
  children: [
    { headerName: "Request", field: "shop.erc_requested", minWidth: 130, cellRenderer: DateFormatRenderer },
    { headerName: "Schedule", field: "shop.erc_scheduled", minWidth: 130, cellRenderer: DateFormatRenderer },
    { headerName: "Released", field: "erc_released", minWidth: 130, cellRenderer: DateFormatRenderer }
  ]
},
{
  headerName: "",
  children: [
    { 
      headerName: 'Detailer',
      field: 'detailer',
      minWidth: 90
    },
    { 
      headerName: 'Checker',
      field: 'checker',
      minWidth: 120
    },
    { 
      headerName: "CSE OE",
      field: "building.cse_order_entry",
      minWidth: 120
    },
    { 
      headerName: "CSE OM",
      field: "building.cse_order_management",
      minWidth: 120
    },
    { 
      headerName: 'Sales unit',
      field: 'sales_unit.description',
      minWidth: 120
    },
    { 
      headerName: 'Region',
      field: 'sales_unit.region',
      minWidth: 120
    },
    { 
      headerName: 'Note',
      field: 'eng_notes',
      minWidth: 180
    },
    { 
      headerName: 'Created by',
      field: 'created_by',
      minWidth: 120
    },
    { 
      headerName: 'Created date',
      field: 'created_date',
      minWidth: 150
    }
  ]
}
];

const ShopByChangeOrderPage = () => {
  const dispatch = useDispatch();
  const {
    shopByChangeOrderData: { data = [], totalRows = 0, totalPagesCount = 1, exporting = false },
  } = useSelector((s) => s.crm_report);
  const { activeSalesUnitsData = [] } = useSelector((s) => s.crm_sales_unit);

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [sort, setSort] = useState({ attribute: 'id', direction: 'desc' });
  const [search, setSearch] = useState('');
  const [filterValues, setFilterValues] = useState(() => getEmptyValues(FILTER_FIELDS));
  const [filterTags, setFilterTags] = useState([]);
  const [showFilter, setShowFilter] = useState(false);
  const [loading, setLoading] = useState(false);

  const [quickPhaseName, setQuickPhaseName] = useState('');
  const [quickShopReqDate, setQuickShopReqDate] = useState({ startDate: THIS_MONTH_START, endDate: THIS_MONTH_END });
  const [quickShopSchDate, setQuickShopSchDate] = useState({ startDate: null, endDate: null });
  const [quickShopRelDate, setQuickShopRelDate] = useState({ startDate: null, endDate: null });
  const [quickSalesUnitIds, setQuickSalesUnitIds] = useState([]);

  const activeFilterParamsRef = useRef({});
  const searchRef = useRef('');
  const quickPhaseNameRef = useRef('');
  const quickShopReqDateRef = useRef({ startDate: THIS_MONTH_START, endDate: THIS_MONTH_END });
  const quickShopSchDateRef = useRef({ startDate: null, endDate: null });
  const quickShopRelDateRef = useRef({ startDate: null, endDate: null });
  const quickSalesUnitIdsRef = useRef([]);

  const totalPages = totalPagesCount || Math.ceil(totalRows / perPage) || 1;

  const buildQuickParams = () => {
    const p = {};
    if (quickPhaseNameRef.current) p.phase_name_cont = quickPhaseNameRef.current;
    const req = quickShopReqDateRef.current;
    if (req.startDate) p.phase_shop_requested_gteq = req.startDate;
    if (req.endDate)   p.phase_shop_requested_lteq = req.endDate;
    const sch = quickShopSchDateRef.current;
    if (sch.startDate) p.phase_shop_scheduled_gteq = sch.startDate;
    if (sch.endDate)   p.phase_shop_scheduled_lteq = sch.endDate;
    const rel = quickShopRelDateRef.current;
    if (rel.startDate) p.phase_shop_released_gteq = rel.startDate;
    if (rel.endDate)   p.phase_shop_released_lteq = rel.endDate;
    if (quickSalesUnitIdsRef.current.length) p.sales_unit_id_in = quickSalesUnitIdsRef.current;
    return p;
  };

  const buildParams = useCallback(
    (overrides = {}) => ({
      page, per_page: perPage, sort,
      ...(searchRef.current ? { [SEARCH_FIELD]: searchRef.current } : {}),
      ...buildQuickParams(),
      ...activeFilterParamsRef.current,
      ...overrides,
    }),
    [page, perPage, sort]
  );

  const fetchData = useCallback(async (params) => {
    setLoading(true);
    try { await dispatch(getShopByChangeOrder(params)); } finally { setLoading(false); }
  }, [dispatch]);

  useEffect(() => { fetchData(buildParams()); }, [buildParams, fetchData]);
  useEffect(() => { dispatch(getActiveSalesUnits()); }, [dispatch]);

  const debouncedFetch = useRef(debounce((params) => fetchData(params), 500)).current;

  const handleSearchChange = (e) => {
    const val = e.target.value;
    searchRef.current = val;
    setSearch(val);
    debouncedFetch({ page: 1, per_page: perPage, sort, ...(val ? { [SEARCH_FIELD]: val } : {}), ...buildQuickParams(), ...activeFilterParamsRef.current });
  };

  const handleClearSearch = () => {
    searchRef.current = '';
    setSearch('');
    fetchData({ page: 1, per_page: perPage, sort, ...buildQuickParams(), ...activeFilterParamsRef.current });
  };

  const handleSort = useCallback(({ colId, sort: dir }) => {
    const newSort = dir ? { attribute: colId, direction: dir } : { attribute: 'id', direction: 'desc' };
    setSort(newSort);
    setPage(1);
    fetchData({ page: 1, per_page: perPage, sort: newSort, ...(searchRef.current ? { [SEARCH_FIELD]: searchRef.current } : {}), ...buildQuickParams(), ...activeFilterParamsRef.current });
  }, [fetchData, perPage]);

  const handleApplyFilter = (params, tags) => {
    activeFilterParamsRef.current = params;
    setFilterTags(tags);
    setPage(1);
    setShowFilter(false);
    fetchData({ page: 1, per_page: perPage, sort, ...(searchRef.current ? { [SEARCH_FIELD]: searchRef.current } : {}), ...buildQuickParams(), ...params });
  };

  const handleResetFilter = () => {
    activeFilterParamsRef.current = {};
    setFilterValues(getEmptyValues(FILTER_FIELDS));
    setFilterTags([]);
    setPage(1);
    fetchData({ page: 1, per_page: perPage, sort, ...(searchRef.current ? { [SEARCH_FIELD]: searchRef.current } : {}), ...buildQuickParams() });
  };

  const removeTag = (tag) => {
    const newParams = Object.fromEntries(Object.entries(activeFilterParamsRef.current).filter(([k]) => k !== tag.id));
    activeFilterParamsRef.current = newParams;
    setFilterTags((prev) => prev.filter((t) => t.id !== tag.id));
    setPage(1);
    fetchData({ page: 1, per_page: perPage, sort, ...(searchRef.current ? { [SEARCH_FIELD]: searchRef.current } : {}), ...buildQuickParams(), ...newParams });
  };

  const handlePhaseNameChange = (e) => {
    const val = e.target.value;
    quickPhaseNameRef.current = val;
    setQuickPhaseName(val);
    debouncedFetch({ page: 1, per_page: perPage, sort, ...(searchRef.current ? { [SEARCH_FIELD]: searchRef.current } : {}), ...buildQuickParams(), ...activeFilterParamsRef.current });
  };

  const handleShopReqDateChange = ({ startDate, endDate }) => {
    quickShopReqDateRef.current = { startDate, endDate };
    setQuickShopReqDate({ startDate, endDate });
    fetchData({ page: 1, per_page: perPage, sort, ...(searchRef.current ? { [SEARCH_FIELD]: searchRef.current } : {}), ...buildQuickParams(), ...activeFilterParamsRef.current });
  };

  const handleShopSchDateChange = ({ startDate, endDate }) => {
    quickShopSchDateRef.current = { startDate, endDate };
    setQuickShopSchDate({ startDate, endDate });
    fetchData({ page: 1, per_page: perPage, sort, ...(searchRef.current ? { [SEARCH_FIELD]: searchRef.current } : {}), ...buildQuickParams(), ...activeFilterParamsRef.current });
  };

  const handleShopRelDateChange = ({ startDate, endDate }) => {
    quickShopRelDateRef.current = { startDate, endDate };
    setQuickShopRelDate({ startDate, endDate });
    fetchData({ page: 1, per_page: perPage, sort, ...(searchRef.current ? { [SEARCH_FIELD]: searchRef.current } : {}), ...buildQuickParams(), ...activeFilterParamsRef.current });
  };

  const handleSalesUnitChange = (ids) => {
    quickSalesUnitIdsRef.current = ids;
    setQuickSalesUnitIds(ids);
    fetchData({ page: 1, per_page: perPage, sort, ...(searchRef.current ? { [SEARCH_FIELD]: searchRef.current } : {}), ...buildQuickParams(), ...activeFilterParamsRef.current });
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-3 tw-p-4">

      {/* Header */}
      <div className="tw-mb-1">
        <div className="tw-flex tw-items-center tw-justify-between tw-flex-wrap tw-gap-3">
          <div className="tw-flex tw-items-center tw-gap-3">
            <div className="tw-w-1.5 tw-h-10 tw-rounded-sm tw-flex-shrink-0" style={{ background: 'linear-gradient(to bottom, #0ea5e9, #6366f1)' }} />
            <div className="tw-flex tw-items-center tw-gap-2">
              <MdCompareArrows className="tw-text-blue-600" size={22} />
              <div>
                <h1 className="tw-mb-0 tw-font-bold tw-text-2xl tw-text-gray-900">Shop by Change Order</h1>
                <p className="tw-text-gray-500 tw-text-sm tw-mb-0">Shop drawing schedule tracking by change order phases.</p>
              </div>
            </div>
          </div>

          <div className="tw-flex tw-items-center tw-gap-2">
            <button type="button" onClick={() => fetchData(buildParams())} disabled={loading} className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-sm tw-border tw-border-slate-300 tw-bg-white tw-text-slate-600 hover:tw-bg-slate-50 tw-text-sm tw-cursor-pointer disabled:tw-opacity-50">
              <FaSyncAlt size={12} className={loading ? 'tw-animate-spin' : ''} />
              Reload
            </button>
            <button type="button" onClick={() => dispatch(exportShopByChangeOrder(buildParams()))} disabled={exporting || loading} className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-sm tw-border tw-border-green-300 tw-bg-green-50 tw-text-green-700 hover:tw-bg-green-100 tw-text-sm tw-font-medium tw-cursor-pointer disabled:tw-opacity-50">
              <FaFileExcel size={13} />
              {exporting ? 'Exporting...' : 'Export'}
            </button>
          </div>
        </div>
      </div>

      {/* Filter bar */}
      <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-slate-200 tw-overflow-hidden">
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-px-3 tw-py-2.5">
          {/* J number search */}
          <div className="tw-relative tw-flex tw-items-center tw-w-full sm:tw-w-40 sm:tw-flex-shrink-0">
            <FaSearch className="tw-absolute tw-left-3 tw-text-slate-400 tw-pointer-events-none" size={12} />
            <input
              type="text"
              value={search}
              onChange={handleSearchChange}
              placeholder="J number..."
              className="tw-w-full tw-pl-8 tw-pr-7 tw-py-1.5 tw-text-sm tw-border tw-border-slate-200 tw-rounded focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary-400 focus:tw-border-transparent tw-bg-slate-50 hover:tw-bg-white tw-transition-colors tw-text-slate-700 tw-placeholder-slate-400"
            />
            {search && (
              <button type="button" onClick={handleClearSearch} className="tw-absolute tw-right-2 tw-flex tw-items-center tw-justify-center tw-w-4 tw-h-4 tw-rounded-xl tw-bg-slate-300 hover:tw-bg-slate-400 tw-text-white tw-border-0 tw-cursor-pointer tw-transition-colors tw-p-0">
                <FaTimes size={8} />
              </button>
            )}
          </div>

          <div className="tw-hidden sm:tw-block tw-h-5 tw-w-px tw-bg-slate-200 tw-flex-shrink-0" />

          {/* Phase name */}
          <div className="tw-relative tw-flex tw-items-center tw-w-full sm:tw-w-36 sm:tw-flex-shrink-0">
            <input
              type="text"
              value={quickPhaseName}
              onChange={handlePhaseNameChange}
              placeholder="Phase..."
              className="tw-w-full tw-px-3 tw-py-1.5 tw-text-sm tw-border tw-border-slate-200 tw-rounded focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary-400 focus:tw-border-transparent tw-bg-slate-50 hover:tw-bg-white tw-transition-colors tw-text-slate-700 tw-placeholder-slate-400"
            />
            {quickPhaseName && (
              <button type="button" onClick={() => handlePhaseNameChange({ target: { value: '' } })} className="tw-absolute tw-right-2 tw-flex tw-items-center tw-justify-center tw-w-4 tw-h-4 tw-rounded-xl tw-bg-slate-300 hover:tw-bg-slate-400 tw-text-white tw-border-0 tw-cursor-pointer tw-transition-colors tw-p-0">
                <FaTimes size={8} />
              </button>
            )}
          </div>

          <div className="tw-hidden sm:tw-block tw-h-5 tw-w-px tw-bg-slate-200 tw-flex-shrink-0" />

          {/* Shop dates */}
          <DateRangePicker
            label="Shop Req."
            startDate={quickShopReqDate.startDate}
            endDate={quickShopReqDate.endDate}
            onChange={handleShopReqDateChange}
          />
          <DateRangePicker
            label="Shop Sch."
            startDate={quickShopSchDate.startDate}
            endDate={quickShopSchDate.endDate}
            onChange={handleShopSchDateChange}
          />
          <DateRangePicker
            label="Shop Rel."
            startDate={quickShopRelDate.startDate}
            endDate={quickShopRelDate.endDate}
            onChange={handleShopRelDateChange}
          />

          {/* Sales unit */}
          <SalesUnitGroupedDropdown
            units={activeSalesUnitsData}
            selectedIds={quickSalesUnitIds}
            onChange={handleSalesUnitChange}
          />

          <div className="tw-hidden sm:tw-block tw-h-5 tw-w-px tw-bg-slate-200 tw-flex-shrink-0" />

          <button
            type="button"
            onClick={() => setShowFilter(true)}
            className={`tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded tw-border tw-text-sm tw-font-medium tw-cursor-pointer tw-transition-all tw-flex-shrink-0 ${
              filterTags.length > 0
                ? 'tw-bg-primary-600 tw-border-primary-600 tw-text-white hover:tw-bg-primary-700'
                : 'tw-bg-white tw-border-slate-200 tw-text-slate-600 hover:tw-border-primary-300 hover:tw-text-primary-600 hover:tw-bg-primary-50'
            }`}
          >
            <MdFilterAlt size={15} />
            More Filters
            {filterTags.length > 0 && (
              <span className="tw-inline-flex tw-items-center tw-justify-center tw-min-w-[18px] tw-h-[18px] tw-px-1 tw-rounded-xl tw-bg-white/30 tw-text-white tw-text-xs tw-font-bold tw-leading-none">
                {filterTags.length}
              </span>
            )}
          </button>

          {!loading && totalRows > 0 && (
            <span className="tw-ml-auto tw-flex-shrink-0 tw-text-xs tw-text-slate-400 tw-font-medium">
              {totalRows.toLocaleString()} records
            </span>
          )}
        </div>

        {filterTags.length > 0 && (
          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-border-t tw-border-slate-100 tw-bg-primary-50/40">
            <span className="tw-text-xs tw-text-slate-400 tw-font-medium tw-flex-shrink-0">Active filters:</span>
            {filterTags.map((tag) => (
              <span key={tag.id} className="tw-inline-flex tw-items-center tw-gap-1 tw-pl-2.5 tw-pr-1 tw-py-0.5 tw-bg-white tw-border tw-border-primary-200 tw-text-slate-700 tw-rounded-md tw-text-xs tw-shadow-sm">
                <span className="tw-text-primary-500 tw-font-semibold">{tag.name}</span>
                <span className="tw-text-slate-300 tw-mx-0.5">·</span>
                <span className="tw-max-w-[140px] tw-truncate tw-text-slate-600">{Array.isArray(tag.value) ? tag.value.join(' – ') : tag.value}</span>
                <button type="button" onClick={() => removeTag(tag)} className="tw-ml-0.5 tw-flex tw-items-center tw-justify-center tw-w-4 tw-h-4 tw-rounded-sm tw-text-slate-400 hover:tw-bg-red-100 hover:tw-text-red-500 tw-bg-transparent tw-border-0 tw-cursor-pointer tw-transition-colors">
                  <FaTimes size={8} />
                </button>
              </span>
            ))}
            <button type="button" onClick={handleResetFilter} className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded-md tw-text-xs tw-text-red-500 hover:tw-bg-red-500 hover:tw-text-white tw-border tw-border-red-200 hover:tw-border-red-500 tw-bg-transparent tw-cursor-pointer tw-transition-colors tw-font-medium">
              <FaTimes size={8} />
              Clear all
            </button>
          </div>
        )}
      </div>

      {/* Grid */}
      <div className="tw-bg-white tw-rounded tw-shadow-sm tw-border tw-border-slate-200 tw-overflow-hidden">
        <AgGridTable
          rowData={data}
          columnDefs={COLUMN_DEFS}
          loading={loading}
          pagination={false}
          filter={false}
          height="max(320px, calc(100dvh - 290px))"
          onSortChanged={handleSort}
        />
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={page}
        totalPages={totalPages}
        totalResults={totalRows}
        perPage={perPage}
        pageSizeOptions={[20, 50, 100, 200]}
        onPageChange={setPage}
        onPageSizeChange={(s) => { setPerPage(s); setPage(1); }}
        disabled={loading}
      />

      <AdvanceFilterDrawer
        show={showFilter}
        onClose={() => setShowFilter(false)}
        onApply={handleApplyFilter}
        onReset={handleResetFilter}
        fields={FILTER_FIELDS}
        values={filterValues}
        onChange={(k, v) => setFilterValues((prev) => ({ ...prev, [k]: v }))}
        title="Advanced Filters"
      />
    </div>
  );
};

export default ShopByChangeOrderPage;
